// SEO Component using React 19's native document metadata support
// React 19+ automatically hoists <title>, <meta>, and <link> tags to <head>

import { useEffect } from 'react';

const SITE_URL = 'https://ecoverasourcing.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_sourcingxperts/artifacts/i24d4eko_logo-png.png';

export const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  keywords,
  structuredData,
  noindex = false
}) => {
  // Ensure title is always a string
  const safeTitle = typeof title === 'string' && title.trim() ? title.trim() : '';
  const fullTitle = safeTitle
    ? `${safeTitle} | Ecovera Sourcing LLP`
    : 'Ecovera Sourcing LLP - End-to-End Sourcing & Trading Solutions';
  
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const safeDescription = description || 'Leading sourcing and trading partner delivering seamless product solutions from concept to delivery.';

  // Use useEffect to set document title directly for better browser compatibility
  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  // Prepare structured data JSON string
  const getStructuredDataJson = () => {
    if (!structuredData) return null;
    
    try {
      if (Array.isArray(structuredData)) {
        return JSON.stringify({
          "@context": "https://schema.org",
          "@graph": structuredData.map(item => {
            const { "@context": _, ...rest } = item;
            return rest;
          })
        });
      }
      return JSON.stringify(structuredData);
    } catch (e) {
      console.warn('Failed to stringify structured data:', e);
      return null;
    }
  };

  const structuredDataJson = getStructuredDataJson();
  const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  // React 19 automatically hoists these tags to <head>
  // Note: We use useEffect for title to ensure proper browser tab display
  return (
    <>
      <meta name="description" content={safeDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      <meta name="robots" content={robotsContent} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Ecovera Sourcing LLP" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional */}
      <meta name="author" content="Ecovera Sourcing LLP" />
      <meta name="geo.region" content="IN-UP" />
      <meta name="geo.placename" content="Noida" />
      
      {/* Structured Data */}
      {structuredDataJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: structuredDataJson }}
        />
      )}
    </>
  );
};

// Structured Data Templates
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Ecovera Sourcing LLP",
  "alternateName": "Ecovera Sourcing",
  "url": "https://ecoverasourcing.com",
  "logo": DEFAULT_IMAGE,
  "description": "Leading end-to-end sourcing and trading partner delivering seamless product solutions from concept to delivery across India.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "C30, C Block, Sector 63",
    "addressLocality": "Noida",
    "postalCode": "201301",
    "addressRegion": "Uttar Pradesh",
    "addressCountry": "IN"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-9057905733",
    "contactType": "customer service",
    "email": "info@ecoverasourcing.com",
    "availableLanguage": ["English", "Hindi"]
  },
  "sameAs": [
    "https://www.linkedin.com/company/ecoverasourcing",
    "https://twitter.com/ecoverasourcing"
  ]
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Ecovera Sourcing LLP",
  "image": DEFAULT_IMAGE,
  "url": "https://ecoverasourcing.com",
  "telephone": "+91-9057905733",
  "email": "info@ecoverasourcing.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "C30, C Block, Sector 63",
    "addressLocality": "Noida",
    "postalCode": "201301",
    "addressRegion": "Uttar Pradesh",
    "addressCountry": "IN"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "10:00",
      "closes": "14:00"
    }
  ],
  "priceRange": "$$"
};

export const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Product Sourcing and Supply Chain Management",
  "provider": {
    "@type": "Organization",
    "name": "Ecovera Sourcing LLP"
  },
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Sourcing Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Product Sourcing & Procurement"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Supplier Discovery & Vendor Management"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Quality Control & Inspection"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Supply Chain Management"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Customized Product Development"
        }
      }
    ]
  }
};

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://ecoverasourcing.com${item.path}`
  }))
});

export default SEO;
